import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Bookmark, Award, ArrowRight, ShieldAlert, RotateCw } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { TID } from "../constants/testIds";
import { toast } from "sonner";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const { user, resendVerification, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [verifyGate, setVerifyGate] = useState(false);

  useEffect(() => {
    if (user === false) {
      navigate(lp("/login"));
      return;
    }
    if (user && user.email_verified === false) {
      setVerifyGate(true);
      return;
    }
    if (user) {
      api
        .get("/me/dashboard")
        .then((r) => setData(r.data))
        .catch((e) => {
          const detail = e.response?.data?.detail;
          if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
            setVerifyGate(true);
          } else {
            toast.error(formatApiError(detail));
          }
        });
    }
  }, [user, navigate, lp]);

  const onResend = async () => {
    if (!user) return;
    const r = await resendVerification(user.email);
    if (r.ok) {
      toast.success(lang === "en" ? "Verification email sent." : "Email de confirmare trimis.");
      navigate(lp("/check-inbox"), { state: { email: user.email } });
    }
  };

  if (!user || user === null) {
    return (
      <div className="container py-32 text-center text-cream-dim font-mono-label">
        {t.common.loading}
      </div>
    );
  }

  if (verifyGate) {
    return (
      <div className="container py-16 md:py-24 max-w-xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass rounded-3xl p-10 md:p-12"
        >
          <ShieldAlert className="h-10 w-10 text-gold" strokeWidth={1.5} />
          <h1 className="mt-8 font-display text-h1 tracking-tight">
            {lang === "en" ? "Verify your email to continue." : "Confirmă emailul pentru a continua."}
          </h1>
          <p className="mt-3 text-cream-muted">
            {lang === "en"
              ? "Your account is created but not yet verified. Open the email we sent and click the verification button."
              : "Contul tău a fost creat dar nu este confirmat. Deschide emailul trimis și apasă pe butonul de confirmare."}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={onResend} className="btn btn-gold">
              <RotateCw className="h-4 w-4" />
              {lang === "en" ? "Resend verification email" : "Retrimite emailul de confirmare"}
            </button>
            <button onClick={logout} className="btn btn-ghost">
              {t.nav.logout}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container py-12 md:py-20">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <p className="font-mono-label text-gold/80 mb-3">{t.dashboard.kicker}</p>
        <h1
          data-testid={TID.dashboardWelcome}
          className="font-display text-section md:text-section-lg tracking-tight"
        >
          {t.dashboard.welcome} <span className="ed-italic text-gold">{user.name}</span>
        </h1>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={<Flame className="h-5 w-5 text-gold" />} label={t.dashboard.streak} value={data?.streak ?? 0} />
        <StatCard icon={<Bookmark className="h-5 w-5 text-gold" />} label={t.dashboard.purchased} value={data?.purchased?.length ?? 0} />
        <StatCard icon={<Award className="h-5 w-5 text-gold" />} label={t.dashboard.achievements} value={data?.achievements?.length ?? 0} />
      </div>

      <section className="mt-16">
        <h2 className="font-mono-label text-gold/80 mb-6">{t.dashboard.purchased}</h2>
        {data?.purchased?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.purchased.map((g) => (
              <Link key={g.slug} to={lp(`/guides/${g.slug}`)} className="glass glass-hover rounded-2xl p-6">
                <h3 className="font-display text-card">{g.translations[lang].title}</h3>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState
            message={t.dashboard.noGuides}
            ctaLabel={t.dashboard.browseGuides}
            to={lp("/guides")}
          />
        )}
      </section>

      <section className="mt-16">
        <h2 className="font-mono-label text-gold/80 mb-6">{t.dashboard.achievements}</h2>
        <EmptyState message={t.dashboard.noAch} ctaLabel={t.assessment.start} to={lp("/assessment")} />
      </section>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="glass rounded-2xl p-6 flex items-center gap-5">
      <div className="h-12 w-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="font-mono-label text-cream-dim">{label}</div>
        <div className="font-display text-card-lg">{value}</div>
      </div>
    </div>
  );
}

function EmptyState({ message, ctaLabel, to }) {
  return (
    <div className="glass rounded-2xl p-10 text-center">
      <p className="text-cream-muted mb-6">{message}</p>
      <Link to={to} className="btn btn-gold">
        {ctaLabel} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
